import { useState, useLayoutEffect } from "react";
import { login } from "../api/auth";
import { useNavigate } from "react-router-dom";
import { armIntro, prefersReducedMotion } from "../motion/introMotion";
import { setForcedTheme } from "../theme/ThemeProvider";

import emtlogo from "../images/logo.svg";

import Icon from "../components/em/Icon";

// Brand marks — lucide has no vendor logos, so the official multi-colour
// glyphs are inlined here for the SSO buttons.
const MicrosoftMark = () => (
  <svg
    className="brand-ic"
    width="14"
    height="14"
    viewBox="0 0 23 23"
    aria-hidden="true"
  >
    <path fill="#f25022" d="M1 1h10v10H1z" />
    <path fill="#7fba00" d="M12 1h10v10H12z" />
    <path fill="#00a4ef" d="M1 12h10v10H1z" />
    <path fill="#ffb900" d="M12 12h10v10H12z" />
  </svg>
);

const GoogleMark = () => (
  <svg
    className="brand-ic"
    width="14"
    height="14"
    viewBox="0 0 48 48"
    aria-hidden="true"
  >
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const Login = () => {
  // The login page is always light — the forced theme wins over the stored
  // preference, the server-synced preference, and OS dark-mode changes.
  useLayoutEffect(() => {
    setForcedTheme("light");
    return () => setForcedTheme(null);
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ password: "" });
  const [leaving, setLeaving] = useState(false);

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const getDefaultRoute = (role, permissions = []) => {
    let basePath = "";

    if (role === "Admin") {
      basePath = "/admin";
    } else if (role === "HR") {
      basePath = "/hr";
    } else if (role === "member") {
      basePath = "/member";
    } else {
      // Unknown / null role — send to unauthorized page
      return "/unauthorized";
    }

    // Walk the permission priority list — first match wins
    if (permissions.includes("view_dashboard")) return `${basePath}/dashboard`;
    if (permissions.includes("view_employees")) return `${basePath}/teams`;
    if (permissions.includes("view_calendar")) return `${basePath}/calendar`;
    if (
      permissions.includes("view_leaves") ||
      permissions.includes("apply_leave")
    )
      return `${basePath}/leaves`;
    if (permissions.includes("view_settings")) return `${basePath}/settings`;

    // Fallback: send to dashboard anyway — PermissionRoute will show a
    // proper page if the route is truly denied, not a blank screen.
    return `${basePath}/dashboard`;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await login(form);
      const canonicalRole = res.data.role;
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("role", res.data.role);
      localStorage.setItem("userdata", JSON.stringify(res.data.user));
      //alert('Logged in successfully as ' + res.data.role);
      localStorage.setItem(
        "permissions",
        JSON.stringify(res.data.permissions || []),
      );

      // Strip user.role to avoid confusion — store display name separately
      const safeUserData = {
        ...res.data.user,
        role: canonicalRole, // ✅ overwrite with canonical, not "Employee"
      };
      localStorage.setItem("userdata", JSON.stringify(safeUserData));

      // Tell the app-wide BackgroundProvider (mounted above the router, so it
      // never unmounts on navigation) to re-pull the now-authenticated library
      // + this user's chosen background without needing a hard refresh.
      window.dispatchEvent(new Event("emhub:login"));

      // First-login gate: new member/HR accounts owe the onboarding wizard
      // before they can use the app. Admin accounts never go through it.
      const owesOnboarding =
        res.data.user?.profile_completed === 0 &&
        (canonicalRole === "member" || canonicalRole === "HR");
      const redirectPath = owesOnboarding
        ? "/start"
        : getDefaultRoute(res.data.role, res.data.permissions || []);

      // Cinematic hand-over: dissolve the login card first, arm the
      // dashboard intro, then swap routes mid-fade so it reads as one shot.
      if (prefersReducedMotion()) {
        navigate(redirectPath, { replace: owesOnboarding });
        return;
      }
      setLeaving(true);
      armIntro();
      setTimeout(
        () => navigate(redirectPath, { replace: owesOnboarding }),
        620,
      );
    } catch (error) {
      alert(error.response?.data?.error || "Login failed");
    }
  };

  return (
    <div className={`login-bg-wrap${leaving ? " login-leaving" : ""}`}>
      <video
        className="login-bg-video"
        src="/videos/login-bg.mp4"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
      />
      <div className="login-bg-overlay" aria-hidden="true" />
      <div className="container">
        <div className="login-page">
          <div className="item">
            <div className="signin-wrap">
              <div className="signin-form">
                <div className="elogo elogo-inside">
                  <img src={emtlogo} alt="Logo" />
                </div>
                <div className="title">
                  <h3>Sign In</h3>
                  <p>Sign in to your account to continuee</p>
                </div>
                {/* SSO sign-in is hidden for now — restore this block (and the
                    OR divider) when Microsoft/Google login goes live. */}
                {false && (
                  <>
                    <div className="mi-gole">
                      <p>
                        <a href="#">
                          <MicrosoftMark />
                          Sign in with Microsoft
                        </a>
                      </p>
                      <p>
                        <a href="#">
                          <GoogleMark />
                          Sign in with Google
                        </a>
                      </p>
                    </div>
                    <div className="line">
                      <span>OR</span>
                    </div>
                  </>
                )}
                <div className="login-form">
                  <form onSubmit={handleSubmit} className="loginwrap">
                    <div className="form-item">
                      <label>Email Address</label>
                      <input
                        name="email"
                        placeholder="Email"
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-item eye-icon">
                      <label className="pas-wrap">
                        Password <a href="#">Forgot password</a>
                      </label>
                      <input
                        name="password"
                        placeholder="Password"
                        type={showPassword ? "text" : "password"}
                        onChange={handleChange}
                        required
                      />

                      <div
                        className="pass-show"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <Icon name={showPassword ? "eye" : "eyeslash"} />
                      </div>
                    </div>
                    <div className="form-action">
                      <button type="submit" className="btn-orange">
                        Login
                      </button>
                    </div>
                    <p className="siup-txt">
                      Don't have an account?{" "}
                      {/* Sign-up is disabled — accounts are created by invitation only. */}
                      <span
                        aria-disabled="true"
                        title="Sign-up is disabled"
                        style={{
                          opacity: 0.5,
                          cursor: "not-allowed",
                          textDecoration: "none",
                        }}
                      >
                        Sign Up
                      </span>
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
